import React, { useRef, useState } from 'react';
import AppHeader from './AppHeader';
import {
  lookupGateCredential,
  logGateEntry,
  uploadGateTicketPhoto,
  uploadGateSeasonPassPhoto,
} from '../api';

function Gate({ user, onLogout, onNavigate }) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [admitting, setAdmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [photoTick, setPhotoTick] = useState(0);
  const fileInputRef = useRef(null);
  const queryInputRef = useRef(null);

  const focusSearch = () => {
    window.setTimeout(() => queryInputRef.current?.focus(), 0);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    const trimmed = query.trim();
    if (!trimmed) {
      setResult(null);
      setError('Enter a ticket or season pass number');
      return;
    }
    setLoading(true);
    try {
      const data = await lookupGateCredential(trimmed);
      setResult(data.result);
      setPhotoTick(Date.now());
    } catch (err) {
      setResult(null);
      setError(err.error || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !result) return;
    setUploading(true);
    setError('');
    setMessage('');
    try {
      let data;
      if (result.type === 'season_pass') {
        data = await uploadGateSeasonPassPhoto(result.recordId, file);
      } else {
        data = await uploadGateTicketPhoto(result.credential, file);
      }
      setResult(data.result);
      setPhotoTick(Date.now());
      setMessage('Photo saved');
    } catch (err) {
      setError(err.error || 'Unable to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleAdmit = async ({ force = false } = {}) => {
    if (!result) return;
    setAdmitting(true);
    setError('');
    setMessage('');
    try {
      const data = await logGateEntry({
        credential: result.credential,
        takenBy: user?.id,
        takenByName: user?.name,
        location: user?.location,
        force,
      });
      setMessage(`Entry logged for ${data.result.guestName}`);
      setResult(null);
      setQuery('');
      focusSearch();
    } catch (err) {
      setError(err.error || 'Unable to log entry');
      if (err.result) setResult(err.result);
    } finally {
      setAdmitting(false);
    }
  };

  return (
    <div className="container gate-page">
      <AppHeader
        user={user}
        pageTitle="Ticket Gate"
        subtitle="Scan or enter a ticket or season pass number"
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="gate"
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <form onSubmit={handleSearch} className="gate-search">
        <label htmlFor="gate-query">Ticket or season pass number</label>
        <div className="gate-search-row">
          <input
            ref={queryInputRef}
            id="gate-query"
            type="text"
            autoFocus
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="SFSP-88421 or MATK-…"
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </form>

      {result && (
        <div className="gate-summary">
          <div className="gate-summary-photo">
            {result.photoUrl ? (
              <img
                key={`${result.photoUrl}-${photoTick}`}
                src={`${result.photoUrl}?t=${photoTick}`}
                alt={result.guestName}
                className="gate-photo"
              />
            ) : (
              <div className="gate-photo placeholder">No photo yet</div>
            )}
          </div>
          <h2 className="gate-summary-name">{result.guestName || 'Guest'}</h2>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="user"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />

          <div className="gate-actions">
            <button
              type="button"
              className="btn-secondary"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : result.photoUrl ? 'Update photo' : 'Upload photo'}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={admitting || (!result.usableToday && result.type === 'ticket')}
              onClick={() => handleAdmit({ force: false })}
            >
              {admitting ? 'Logging…' : 'Admit & log entry'}
            </button>
            {!result.usableToday && result.type === 'ticket' && (
              <button
                type="button"
                className="btn-secondary"
                disabled={admitting}
                onClick={() => handleAdmit({ force: true })}
              >
                Force admit
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Gate;
